import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { roleLabel } from '@core/helpers/role-label.helper';
import { MeasurementPoint } from '@core/models/installation.model';
import { InstallationDataSourcesService } from '@core/services/installation-data-sources.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SearchInputComponent,
  TextButtonComponent,
} from '@custom-components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { concatMap, from, tap, toArray } from 'rxjs';
import { AddPointRole } from '../../addable-roles';
import { PlacedRefusal, RefusalPlace, placeRefusal } from '../../editor-refusal';
import { ROLE_GROUP_ICONS, roleGroupKey } from '../../role-groups';

export interface AddPointsDialogData {
  installationOid: string;
  roles: AddPointRole[];
}

interface RoleRow {
  role: string;
  label: string;
  unit: string;
}

interface Category {
  key: string;
  label: string;
  icon: string;
  rows: RoleRow[];
}

interface RoleRowView extends RoleRow {
  added: boolean;
  checked: boolean;
}

interface CategoryView extends Omit<Category, 'rows'> {
  rows: RoleRowView[];
  available: number;
  allChecked: boolean;
  someChecked: boolean;
  expanded: boolean;
}

// Closes with the points it created, or with nothing when cancelled.
@Component({
  selector: 'xemex-add-points-dialog',
  imports: [
    DialogShellComponent,
    FilledButtonComponent,
    MatCheckboxModule,
    MatIconModule,
    SearchInputComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './add-points-dialog.component.html',
  styleUrl: './add-points-dialog.component.scss',
})
export class AddPointsDialogComponent {
  readonly data = inject<AddPointsDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<AddPointsDialogComponent, MeasurementPoint[]>>(MatDialogRef);
  private readonly service = inject(InstallationDataSourcesService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly categories = this.buildCategories();
  readonly query = signal('');
  private readonly collapsed = signal<ReadonlySet<string>>(new Set());
  private readonly selected = signal<ReadonlySet<string>>(new Set());
  private readonly added = signal<ReadonlySet<string>>(
    new Set(this.data.roles.filter(role => role.added).map(role => role.role)),
  );
  private readonly createdCount = signal(0);
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedRefusal | null>(null);
  private readonly gone = computed(() => this.refusal()?.stale ?? false);
  // Read by the opener once the dialog closes: points were added before a refusal, or the
  // installation changed underneath.
  readonly stale = computed(() => this.createdCount() > 0 || this.gone());

  readonly refusalKey = computed(() => this.messageAt('form') ?? this.messageAt('dialog'));

  readonly selectedCount = computed(() => this.selected().size);
  readonly canSave = computed(() => this.selectedCount() > 0 && !this.isSaving() && !this.gone());
  readonly selectedKey = computed(() => {
    const count = this.selectedCount();
    if (count === 0) return 'installations.editor.addPoints.noneSelected';
    return count === 1
      ? 'installations.editor.addPoints.oneSelected'
      : 'installations.editor.addPoints.manySelected';
  });
  readonly addKey = computed(() =>
    this.selectedCount() > 1
      ? 'installations.editor.addPoints.addMany'
      : 'installations.editor.addPoints.addOne',
  );

  // A category shows the rows that match the search; one whose own label matches shows them all.
  // Its checkbox and count follow the rows shown.
  readonly groups = computed<CategoryView[]>(() => {
    const query = this.query().trim().toLowerCase();
    const added = this.added();
    const selected = this.selected();
    const collapsed = this.collapsed();
    return this.categories.flatMap(category => {
      const categoryMatches = !query || category.label.toLowerCase().includes(query);
      const rows = category.rows
        .filter(row => categoryMatches || row.label.toLowerCase().includes(query))
        .map(row => ({ ...row, added: added.has(row.role), checked: selected.has(row.role) }));
      if (rows.length === 0) return [];
      const choosable = rows.filter(row => !row.added);
      const checked = choosable.filter(row => row.checked).length;
      return [
        {
          ...category,
          rows,
          available: choosable.length,
          allChecked: checked === choosable.length,
          someChecked: checked > 0 && checked < choosable.length,
          expanded: !collapsed.has(category.key),
        },
      ];
    });
  });

  private buildCategories(): Category[] {
    const rowsByKey = new Map<string, RoleRow[]>();
    for (const { role, displayName, unit } of this.data.roles) {
      const key = roleGroupKey(role);
      const row = { role, label: roleLabel(this.translate, role, displayName), unit };
      rowsByKey.set(key, [...(rowsByKey.get(key) ?? []), row]);
    }
    return Object.entries(ROLE_GROUP_ICONS).flatMap(([key, icon]) => {
      const rows = rowsByKey.get(key);
      return rows
        ? [{ key, icon, label: this.translate.instant(`installations.roleGroups.${key}`), rows }]
        : [];
    });
  }

  messageAt(place: RefusalPlace): string | null {
    const refusal = this.refusal();
    return refusal?.place === place ? refusal.messageKey : null;
  }

  toggleExpanded(key: string): void {
    this.collapsed.update(keys => toggled(keys, key));
  }

  toggleRole(role: string): void {
    this.selected.update(roles => toggled(roles, role));
  }

  toggleCategory(group: CategoryView): void {
    const choosable = group.rows.filter(row => !row.added).map(row => row.role);
    this.selected.update(roles => {
      const next = new Set(roles);
      for (const role of choosable) {
        if (group.allChecked) next.delete(role);
        else next.add(role);
      }
      return next;
    });
  }

  // One request per point, in list order. A refusal stops the rest; the points made by then stay
  // added and leave the selection.
  save(): void {
    if (!this.canSave()) return;
    const roles = this.categories
      .flatMap(category => category.rows)
      .map(row => row.role)
      .filter(role => this.selected().has(role));
    this.isSaving.set(true);
    this.refusal.set(null);
    from(roles)
      .pipe(
        concatMap(role =>
          this.service
            .createPoint(this.data.installationOid, { role_code: role })
            .pipe(tap(() => this.markAdded(role))),
        ),
        toArray(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: points => this.dialogRef.close(points),
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.refusal.set(placeRefusal(toRefusal(error)));
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private markAdded(role: string): void {
    this.added.update(roles => new Set(roles).add(role));
    this.selected.update(roles => {
      const next = new Set(roles);
      next.delete(role);
      return next;
    });
    this.createdCount.update(count => count + 1);
  }
}

function toggled(set: ReadonlySet<string>, value: string): Set<string> {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
}
